import React, { useEffect, useRef, useState } from 'react';

/**
 * Modal primitives for actions with consequences.
 *
 * The old panel collected a *required audit reason* — the thing that makes an
 * action defensible six months later — in a 24-character text input wedged
 * into a table cell, with no confirmation on the destructive path. If a
 * reason is worth requiring, it is worth a field someone can actually write a
 * sentence in, and worth showing them exactly what they are about to change
 * while they write it.
 */
function Backdrop({ children, onClose, labelledBy, maxWidth = 'max-w-md' }) {
  const ref = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    // Focus the panel so Escape and Tab are scoped here immediately, without
    // the user having to click into it first.
    ref.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/25 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={ref}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className={`w-full ${maxWidth} max-h-[85vh] overflow-y-auto rounded-xl border border-hairline bg-surface p-5 shadow-xl outline-none`}
      >
        {children}
      </div>
    </div>
  );
}

const btnGhost = 'rounded-lg border border-hairline px-3 py-2 text-sm font-medium text-ink-secondary hover:bg-surface-page hover:text-ink';

/**
 * Confirm-only. Use `tone="critical"` for anything expensive, disruptive or
 * hard to undo — force-crawl-all against every brand's live site, say.
 */
export function ConfirmDialog({ open, title, body, confirmLabel = 'Confirm', tone = 'default', onConfirm, onClose }) {
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const run = async () => {
    setBusy(true);
    try { await onConfirm(); } finally { setBusy(false); }
  };

  const confirmCls = tone === 'critical'
    ? 'bg-status-critical text-white hover:brightness-95'
    : 'bg-raspberry-600 text-white hover:bg-raspberry-700';

  return (
    <Backdrop onClose={onClose} labelledBy="confirm-title">
      <h2 id="confirm-title" className="text-base font-semibold text-ink">{title}</h2>
      {body && <div className="mt-2 text-sm leading-relaxed text-ink-secondary">{body}</div>}
      <div className="mt-5 flex justify-end gap-2">
        <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
        <button
          type="button"
          onClick={run}
          disabled={busy}
          className={`rounded-lg px-3 py-2 text-sm font-medium disabled:opacity-50 ${confirmCls}`}
        >
          {busy ? 'Working…' : confirmLabel}
        </button>
      </div>
    </Backdrop>
  );
}

/**
 * Confirm, with a mandatory reason that lands in the audit log.
 *
 * The submit button stays disabled until the reason is non-trivial. A reason
 * of "x" satisfies a `required` check and tells a future reader nothing, so
 * the floor is a real minimum length rather than mere presence.
 */
export function ReasonDialog({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  tone = 'default',
  placeholder = 'Why are you doing this? This is recorded in the audit log.',
  minLength = 4,
  onConfirm,
  onClose,
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setReason(''); }, [open]);
  if (!open) return null;

  const ok = reason.trim().length >= minLength;

  const run = async () => {
    if (!ok) return;
    setBusy(true);
    try { await onConfirm(reason.trim()); } finally { setBusy(false); }
  };

  const confirmCls = tone === 'critical'
    ? 'bg-status-critical text-white hover:brightness-95'
    : 'bg-raspberry-600 text-white hover:bg-raspberry-700';

  return (
    <Backdrop onClose={onClose} labelledBy="reason-title">
      <h2 id="reason-title" className="text-base font-semibold text-ink">{title}</h2>
      {body && <div className="mt-2 text-sm leading-relaxed text-ink-secondary">{body}</div>}
      <label className="mt-4 block">
        <span className="text-xs font-medium text-ink-secondary">Reason</span>
        <textarea
          autoFocus
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={placeholder}
          className="mt-1 w-full resize-y rounded-lg border border-hairline px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-raspberry-300"
        />
      </label>
      <div className="mt-4 flex items-center justify-end gap-2">
        <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
        <button
          type="button"
          onClick={run}
          disabled={!ok || busy}
          className={`rounded-lg px-3 py-2 text-sm font-medium disabled:opacity-40 ${confirmCls}`}
        >
          {busy ? 'Working…' : confirmLabel}
        </button>
      </div>
    </Backdrop>
  );
}

export default Backdrop;
